import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LandingFigure, { ScreenshotGallery } from '../components/LandingFigure';
import { SITE_LAST_UPDATED } from '../data/features';
import { longDate } from '../lib/siteDates';
import {
  ArrowRight, Check, Code, Box, TrendingUp, Heart, Megaphone,
  DollarSign, Briefcase, ChevronRight,
} from 'lucide-react';

// The words of each team page live in public/locales/<lang>/teams.json under the
// team's key (people_ops for /teams/people-ops), with an alt and a caption for each
// screenshot below, in the same order. This keeps what is not text.
const TEAMS = {
  product: { icon: Box, color: 'pink', screenshots: ['/screenshots/projects/kanban.png', '/screenshots/projects/timeline.png'] },
  engineering: { icon: Code, color: 'yellow', screenshots: ['/screenshots/tasks/list.png', '/screenshots/webhooks/main.png'] },
  sales: { icon: TrendingUp, color: 'pink', screenshots: ['/screenshots/projects/kanban.png', '/screenshots/projects/table.png'] },
  marketing: { icon: Megaphone, color: 'blue', screenshots: ['/screenshots/projects/calendar.png', '/screenshots/documents/list.png'] },
  'people-ops': { icon: Heart, color: 'yellow', screenshots: ['/screenshots/documents/list.png', '/screenshots/projects/kanban.png'] },
  finance: { icon: DollarSign, color: 'blue', screenshots: ['/screenshots/projects/table.png', '/screenshots/documents/database.png'] },
  assistants: { icon: Briefcase, color: 'pink', screenshots: ['/screenshots/home/main.png', '/screenshots/tasks/calendar.png'] },
};

const asList = (v) => (Array.isArray(v) ? v : []);

const COLOR_CLASSES = {
  blue:   { pill: 'from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-400',   accent: 'text-blue-400',  ring: 'ring-blue-500/40' },
  yellow: { pill: 'from-amber-500/20 to-amber-600/20 border-amber-500/30 text-amber-400', accent: 'text-amber-400', ring: 'ring-amber-500/40' },
  pink:   { pill: 'from-pink-500/20 to-pink-600/20 border-pink-500/30 text-pink-400',   accent: 'text-pink-400',  ring: 'ring-pink-500/40' },
};

const TeamPage = () => {
  const { t, i18n } = useTranslation(['landing', 'teams']);
  const { teamId } = useParams();
  const id = TEAMS[teamId] ? teamId : 'product';
  const config = TEAMS[id];
  const c = t(`teams:${id.replace(/-/g, '_')}`, { returnObjects: true });
  const text = c && typeof c === 'object' ? c : {};
  const shots = asList(text.screenshots);
  const team = {
    ...text,
    color: config.color,
    benefits: asList(text.benefits),
    faq: asList(text.faq),
    screenshots: config.screenshots.map((src, i) => ({ src, ...shots[i] })),
  };
  const Icon = config.icon;
  const colors = COLOR_CLASSES[team.color] || COLOR_CLASSES.yellow;
  const heroShot = team.screenshots?.[0];

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={team.seo_title || team.page_title || team.title}
        description={team.seo_description}
        keywords={team.keywords}
        canonical={`/teams/${teamId || 'product'}`}
        image={heroShot?.src}
        faqItems={team.faq}
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('team_page.crumb'), url: '/teams/product' },
          { name: team.crumb },
        ]}
      />
      <Navbar />

      <main className="pt-12 pb-24">
        <ScreenshotGallery items={team.screenshots || []}>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 pt-12 pb-12 sm:pt-16">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-white transition-colors">{t('pricing.breadcrumb_home')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/teams/product" className="hover:text-white transition-colors">{t('team_page.crumb')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-300">{team.crumb}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${colors.pill} border mb-6 text-xs font-medium`}>
                <Icon className="w-3.5 h-3.5" />
                <span>{t('team_page.badge')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5 leading-[1.05]">
                {team.title}
              </h1>
              <p className="text-xl text-gray-300 mb-5">{team.subtitle}</p>
              <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-2xl">{team.summary || team.description}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                  {t('team_page.cta_start_free')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
                  {t('team_page.cta_talk_sales')}
                </Link>
              </div>
              {/* A "4.8 / 2,400+ reviews" rating used to sit here. There is no
                  such rating. Fabricated ratings and reviews are prohibited
                  outright by Annex I of Directive 2005/29/EC. */}
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>{t('team_page.last_updated')} {longDate(SITE_LAST_UPDATED, i18n.resolvedLanguage || 'en')}</span>
              </div>
            </div>

            {heroShot && (
              <div className="lg:col-span-6">
                <LandingFigure
                  src={heroShot.src}
                  alt={heroShot.alt}
                  caption={heroShot.caption}
                  eager
                  frame="hero"
                  ring={colors.ring}
                />
              </div>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('team_page.benefits_label')}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('team_page.benefits_headline')}</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {team.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-gray-800">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Second screenshot */}
        {team.screenshots?.[1] && (() => {
          const extras = team.screenshots.slice(1);
          const isSingle = extras.length === 1;
          return (
            <section className="max-w-7xl mx-auto px-4 py-16">
              <div className={isSingle ? 'max-w-4xl mx-auto' : 'grid md:grid-cols-2 gap-6'}>
                {extras.map((s, i) => (
                  <LandingFigure key={i} src={s.src} alt={s.alt} caption={s.caption} />
                ))}
              </div>
            </section>
          );
        })()}

        {/* A customer quote used to render here. Every one of them was
            invented, so the block and its data are gone. It comes back when
            there is a real customer willing to be quoted by name. */}

        {/* FAQ */}
        {team.faq?.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 py-16">
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('team_page.faq_label')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('team_page.faq_headline')}</h2>
            </div>
            <div className="space-y-3">
              {team.faq.map((item, i) => (
                <details key={i} className="group bg-white/5 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                    <h3 className="text-white font-semibold text-base sm:text-lg">{item.question}</h3>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-400 leading-relaxed text-sm sm:text-base">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('team_page.final_cta.headline')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            {t('team_page.final_cta.body')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors">
              {t('team_page.cta_start_free')}
            </Link>
            <Link to="/contact" className="border border-gray-700 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/5 transition-colors">
              {t('team_page.final_cta.contact_sales')}
            </Link>
          </div>
        </section>
        </ScreenshotGallery>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
